import React from "react";
import { Link } from "react-router-dom";
import LandingFooter from "../_components/Footer/LandingFooter";

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navigation Header */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800">
                InviteWith<span className="text-rose-500">Love</span>
              </h1>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex-1">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-lg text-gray-600 bg-gray-100 px-4 py-2 rounded-lg inline-block">
              Last updated: January 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray prose-lg max-w-none">
            <div className="bg-rose-50 border-l-4 border-rose-400 p-6 mb-8 rounded-r-lg">
              <p className="text-rose-800 font-medium mb-2">Important Notice</p>
              <p className="text-rose-700 text-sm leading-relaxed">
                By using our service, you agree to these terms. Please read them carefully. 
                These terms include important information about refunds, liability, and your rights as a consumer.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">1. Agreement to Terms</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                By accessing or using <span className="font-medium text-rose-600">Invite With Love</span> (the "Service"), operated by
                New Axis Labs Limited ("Company," "we," "us," or "our"), you agree to be
                bound by these Terms of Service ("Terms"). If you do not agree, you
                may not use the Service.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">2. Description of Service</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                <span className="font-medium text-rose-600">Invite With Love</span> is a digital invitation platform that allows users
                to create, customise, and send digital invitations for events.
                Features include invitation design tools, guest management, RSVP
                tracking, and related functionality.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">3. User Accounts</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                To use certain features of our Service, you must create an account.
                You agree to:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Provide accurate, current, and complete information</li>
                <li>Keep your account details up to date</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorised use</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">4. Acceptable Use</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed font-medium text-red-700">You agree not to use the Service to:</p>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Violate any applicable laws or regulations</li>
                <li>Send spam or unsolicited communications</li>
                <li>Harass, abuse, or harm others</li>
                <li>Infringe on intellectual property rights</li>
                <li>Upload or transmit viruses, malware, or malicious code</li>
                <li>Attempt to gain unauthorised access to our systems</li>
                <li>Create invitations containing offensive, illegal, or inappropriate content</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">5. Content and Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">Your Content</h3>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                You retain ownership of invitations and other content you create
                using our Service. By using the Service, you grant us a licence to
                use, store, and display your content solely as needed to provide the
                Service.
              </p>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
                <p className="text-amber-800 text-sm">
                  <span className="font-medium">Your Responsibility:</span> You are solely responsible for the content you upload or create,
                  including ensuring you have the necessary rights and permissions
                  (e.g., for guest data and images).
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mt-8 mb-4">Our Intellectual Property</h3>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                The Service, including its original content (excluding user
                content), features, and functionality, is owned by New Axis Labs Limited and
                protected by copyright, trademark, and other intellectual property
                laws.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">6. Payment Terms</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">
                Certain features of the Service require a <span className="font-medium">one-time purchase</span> to
                unlock full functionality for an individual event. By making a
                purchase, you agree to:
              </p>
              <ul className="space-y-2 list-disc list-inside text-gray-700 mb-4">
                <li>Pay all applicable fees as listed on our pricing page</li>
                <li>Provide accurate payment information</li>
                <li>Authorise us (or our payment processor) to charge your chosen payment method</li>
              </ul>
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <p className="text-green-800 text-sm">
                  <span className="font-medium">Currency:</span> All prices are in <span className="font-medium">United States Dollars (USD)</span> unless otherwise
                  stated, and include GST, VAT, or sales tax where applicable.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">7. Refund Policy</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-yellow-800 text-sm leading-relaxed">
                  <span className="font-medium">14-Day Window:</span> Refunds are available within <span className="font-medium">14 days</span> of purchase, provided no
                  invitations have been created using paid features. Once invitations
                  are created using paid features, the purchase is considered used and
                  is non-refundable, except as required under the Consumer Guarantees
                  Act 1993.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">8. Privacy</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="leading-relaxed">
                Your use of the Service is also governed by our <a href="/privacy-policy" className="text-rose-600 hover:text-rose-700 font-medium underline">Privacy Policy</a>,
                which explains how we collect, use, and protect your information.
              </p>
            </div>

          <h2>9. Service Availability</h2>
          <p>
            We strive to keep the Service available and reliable, but we do not
            guarantee uninterrupted or error-free operation. We may suspend
            access for maintenance, upgrades, or issues beyond our reasonable
            control.
          </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">10. Disclaimers and Limitations of Liability</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                <p className="text-red-800 text-sm font-medium mb-2">AS-IS Service</p>
                <p className="text-red-700 text-sm leading-relaxed">
                  The Service is provided on an "as is" basis, without warranties of
                  any kind. To the maximum extent permitted by New Zealand law, we
                  disclaim all warranties, express or implied.
                </p>
              </div>
              <div className="space-y-3 text-gray-700 text-sm">
                <p>In no event shall New Axis Labs Limited be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.</p>
                <p>To the maximum extent permitted by law, our total liability to you for any claim relating to the Service will not exceed the amount you paid for the event purchase giving rise to the claim.</p>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-medium">Consumer Protection:</span> Nothing in these Terms limits your rights under the Consumer Guarantees Act 1993.
                </p>
              </div>
            </div>

          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless New Axis Labs Limited, its officers,
            directors, employees, and agents from any claims, damages, or
            expenses arising from your content, your use of the Service, or your
            violation of these Terms.
          </p>

          <h2>12. Suspension and Termination</h2>
          <p>
            We may suspend or terminate your account immediately, without notice, if we reasonably believe you have breached these Terms, used the Service unlawfully, or created harm to others or to the Service.
          </p>
          <p>
            If your account is terminated, your right to use the Service ends immediately. Purchases are not refundable except as required under the Consumer Guarantees Act 1993.
          </p>

          <h2>13. Force Majeure</h2>
          <p>
            We are not responsible for failure or delay in performance caused by events outside our reasonable control, including natural disasters, internet or hosting outages, strikes, government actions, or third-party service failures.
          </p>

          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of New Zealand. Any disputes shall be resolved in the courts of New Zealand.
          </p>

          <h2>15. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. If changes are material, we will notify you (e.g., by email or notice on the Service). Your continued use after changes means you accept the revised Terms.
          </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200">16. Contact Information</h2>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
              <p className="mb-4 leading-relaxed">For questions about these Terms, please contact us at:</p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <address className="not-italic text-gray-700 space-y-1">
                  <div className="font-medium text-gray-900">New Axis Labs Limited</div>
                  <div>Auckland, New Zealand</div>
                  <div>
                    Email: <a href="mailto:legal@invitewithlove.com" className="text-rose-600 hover:text-rose-700 font-medium">legal@invitewithlove.com</a>
                  </div>
                  <div>
                    Website: <a
                      href="https://newaxislabs.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-600 hover:text-rose-700 font-medium"
                    >
                      newaxislabs.com
                    </a>
                  </div>
                </address>
              </div>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-blue-800 text-sm">
                  <span className="font-medium">Consumer Rights:</span> If you are a consumer as defined under the Consumer Guarantees Act
                  1993, nothing in these Terms limits your rights under that Act.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default TermsOfService;
